import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiFetch } from "@/lib/api";
import {
  Command,
  CommandInput,
  CommandList,
  CommandItem,
  CommandEmpty,
} from "@/components/ui/command";

export interface ClientPickerValue {
  clientId: number;
  fullName: string;
  emailAddress: string | null;
  companyId: number | null;
  companyName: string | null;
}

export interface ClientPickerProps {
  value: ClientPickerValue | null;
  onChange: (v: ClientPickerValue | null) => void;
  placeholder?: string;
}

export function ClientPicker({ value, onChange, placeholder }: ClientPickerProps) {
  const [q, setQ] = useState("");

  const { data } = useQuery({
    queryKey: ["client-finder", q],
    queryFn: () =>
      apiFetch<ClientPickerValue[]>(
        `/api/clients/finder?q=${encodeURIComponent(q)}&take=10`,
      ),
    enabled: q.length >= 2,
    staleTime: 1500,
  });

  return (
    // shouldFilter={false}: we do server-side search via useQuery; cmdk's
    // built-in client-side filter would hide items that don't match the
    // current input value before the async results arrive.
    <Command shouldFilter={false}>
      <CommandInput
        placeholder={placeholder ?? "Search clients..."}
        value={q}
        onValueChange={setQ}
      />
      <CommandList>
        {data && data.length === 0 && <CommandEmpty>No matches</CommandEmpty>}
        {data?.map((r) => (
          <CommandItem key={r.clientId} onSelect={() => onChange(r)}>
            <span>{r.fullName}</span>
            {r.companyName && (
              <span className="ml-2 text-muted-foreground">({r.companyName})</span>
            )}
          </CommandItem>
        ))}
      </CommandList>
      {value && (
        <div className="px-3 py-2 text-sm">
          Selected: <strong>{value.fullName}</strong>
          <button className="ml-2 underline" onClick={() => onChange(null)}>
            Clear
          </button>
        </div>
      )}
    </Command>
  );
}
